import React, { ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';

export type ListTableColumn<T> = {
    id: string;
    header: ReactNode;
    cell: (row: T) => ReactNode;
    className?: string;
    /** Hide column when viewport width is below this value */
    hideBelow?: number;
};

type ListTableProps<T> = {
    columns: ListTableColumn<T>[];
    rows: T[];
    rowKey: (row: T) => string;
    width?: number;
    isLoading?: boolean;
    emptyMessage?: string;
    onRowClick?: (row: T) => void;
    isRowDeleted?: (row: T) => boolean;
    isRowSelected?: (row: T) => boolean;
    className?: string;
    /** Row count above which body is virtualized (default 200). */
    virtualizeThreshold?: number;
};

const DEFAULT_VIRTUALIZE_THRESHOLD = 200;
const ESTIMATED_ROW_HEIGHT = 52;

const stopRowClick = (event: React.MouseEvent) => {
    event.stopPropagation();
};

/**
 * Wrap action cells so row click does not navigate.
 */
export const ListTableActions = ({ children }: { children: ReactNode }) => (
    <div className="list-table__actions" onClick={stopRowClick} onKeyDown={(e) => e.stopPropagation()}>
        {children}
    </div>
);

function ListTable<T>({
    columns,
    rows,
    rowKey,
    width = typeof window !== 'undefined' ? window.innerWidth : 1440,
    isLoading = false,
    emptyMessage,
    onRowClick,
    isRowDeleted,
    isRowSelected,
    className = '',
    virtualizeThreshold = DEFAULT_VIRTUALIZE_THRESHOLD,
}: ListTableProps<T>) {
    const { i18n } = useTranslation();
    const scrollRef = useRef<HTMLDivElement>(null);
    const visibleColumns = columns.filter((col) => !col.hideBelow || width >= col.hideBelow);
    const message = emptyMessage || i18n.t('app.page.listView.empty');
    const shouldVirtualize = !isLoading && rows.length > virtualizeThreshold;

    const rowVirtualizer = useVirtualizer({
        count: shouldVirtualize ? rows.length : 0,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ESTIMATED_ROW_HEIGHT,
        overscan: 12,
    });

    const renderRow = (
        row: T,
        options?: {
            measureRef?: (node: HTMLTableRowElement | null) => void;
            virtualIndex?: number;
        },
    ) => {
        const deleted = isRowDeleted?.(row);
        const selected = isRowSelected?.(row);
        return (
            <tr
                key={rowKey(row)}
                ref={options?.measureRef}
                data-index={options?.virtualIndex}
                className={[
                    'list-table__row',
                    deleted ? 'list-table__row_deleted' : '',
                    selected ? 'list-table__row_selected' : '',
                    onRowClick ? 'list-table__row_clickable' : '',
                ].filter(Boolean).join(' ')}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
                {visibleColumns.map((col) => (
                    <td key={col.id} className={`list-table__td ${col.className || ''}`.trim()}>
                        {col.cell(row)}
                    </td>
                ))}
            </tr>
        );
    };

    const renderBody = () => {
        if (isLoading) {
            return Array.from({ length: 6 }).map((_, index) => (
                <tr key={`skeleton-${index}`} className="list-table__row list-table__row_skeleton">
                    {visibleColumns.map((col) => (
                        <td key={col.id} className={`list-table__td ${col.className || ''}`.trim()}>
                            <span className="list-table__skeleton" />
                        </td>
                    ))}
                </tr>
            ));
        }

        if (rows.length === 0) {
            return (
                <tr className="list-table__row list-table__row_empty">
                    <td className="list-table__td list-table__td_empty" colSpan={visibleColumns.length}>
                        {message}
                    </td>
                </tr>
            );
        }

        if (!shouldVirtualize) {
            return rows.map((row) => renderRow(row));
        }

        const virtualRows = rowVirtualizer.getVirtualItems();
        const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
        const paddingBottom = virtualRows.length > 0
            ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
            : 0;

        return (
            <>
                {paddingTop > 0 ? (
                    <tr aria-hidden="true" className="list-table__row list-table__row_spacer">
                        <td
                            colSpan={visibleColumns.length}
                            style={{ height: paddingTop, padding: 0, border: 0 }}
                        />
                    </tr>
                ) : null}
                {virtualRows.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    return renderRow(row, {
                        virtualIndex: virtualRow.index,
                        measureRef: (node) => {
                            if (node) {
                                rowVirtualizer.measureElement(node);
                            }
                        },
                    });
                })}
                {paddingBottom > 0 ? (
                    <tr aria-hidden="true" className="list-table__row list-table__row_spacer">
                        <td
                            colSpan={visibleColumns.length}
                            style={{ height: paddingBottom, padding: 0, border: 0 }}
                        />
                    </tr>
                ) : null}
            </>
        );
    };

    return (
        <div
            ref={scrollRef}
            className={[
                'list-table-wrapper',
                shouldVirtualize ? 'list-table-wrapper_virtualized' : '',
                className,
            ].filter(Boolean).join(' ')}
        >
            <table className="list-table">
                <thead>
                    <tr>
                        {visibleColumns.map((col) => (
                            <th key={col.id} className={`list-table__th ${col.className || ''}`.trim()}>
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {renderBody()}
                </tbody>
            </table>
        </div>
    );
}

export default ListTable;
